// src/components/ShippingRecord/AddDisputePopin/hooks/useDisputeFormLogic.ts
import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import useShippingRecordDispute from "$hooks/useShippingRecordDispute";
import {
  DispositionType,
  PreparationOrder,
  Shipment,
  ShippingRecordDispute,
  ShippingRecordDisputeType,
  ShippingRecord,
  Media,
} from "$types";
import { getShippingRecordDisputeTypeOptions } from "$utils/typeConstants/ShippingRecordDispute";
import {
  removeDuplicateDeliveryAddresses,
  getDeliveryLocationLabelAlt,
} from "$utils/typeConstants";

export enum EditMode {
  CREATE,
  EDIT,
  READ_ONLY,
}

type FormValues = {
  type: ShippingRecordDisputeType | null;
  text: string;
  linkedEntityId: string | null;
  media: Media[];
};

export const useDisputeFormLogic = (
  shippingRecord: ShippingRecord,
  selectedDispute?: ShippingRecordDispute,
  shippingRecordDisputes: ShippingRecordDispute[] = []
) => {
  let editMode: EditMode = EditMode.CREATE;
  if (selectedDispute) {
    editMode = selectedDispute.closedAt ? EditMode.READ_ONLY : EditMode.EDIT;
  }

  const {
    shippingRecordDispute: dispute,
    createShippingRecordDispute,
    updateShippingRecordDispute,
    isFetching,
  } = useShippingRecordDispute(selectedDispute?.id);

  const defaultFormValues = useMemo(
    (): FormValues => ({
      type: selectedDispute ? dispute?.type : null,
      text: selectedDispute ? dispute?.text : "",
      linkedEntityId: selectedDispute ? dispute?.linkedEntityId : null,
      media: selectedDispute ? dispute?.media : [],
    }),
    [dispute, selectedDispute]
  );

  const form = useForm<FormValues>({
    defaultValues: defaultFormValues,
  });

  const type = form.watch("type");
  const media = form.watch("media");
  const linkedEntityId = form.watch("linkedEntityId");

  const alreadyHasShippingRecordDispute = useMemo(
    () =>
      shippingRecordDisputes?.findIndex(
        (dispute: ShippingRecordDispute) =>
          dispute.type === ShippingRecordDisputeType.SHIPPING_RECORD &&
          !dispute.closedAt
      ) !== -1,
    [shippingRecordDisputes]
  );

  const opsWithNoDispute = useMemo(() => {
    let options = shippingRecord?.shipments
      .map((s) => s.op)
      .filter((op): op is PreparationOrder => Boolean(op)); // Type guard for null values

    if (!options) {
      return [];
    }

    if (editMode === EditMode.CREATE) {
      options = options.filter(
        (op) =>
          !shippingRecordDisputes.some(
            (dispute) => dispute.linkedEntityId === op._id && !dispute.closedAt
          )
      );
    }

    return options.map((op) => ({
      label: op.code,
      value: op._id,
    }));
  }, [shippingRecord?.shipments, shippingRecordDisputes, editMode]);

  const deliveryLocationsWithNoDispute = useMemo(() => {
    const options = shippingRecord?.shipments;
    if (!options) {
      return [];
    }

    const filteredOptions =
      editMode === EditMode.CREATE
        ? options.filter(
            (shipment: Shipment) =>
              !shippingRecordDisputes.some(
                (dispute) =>
                  dispute.linkedEntityId === shipment.toAddress.entityId &&
                  !dispute.closedAt
              )
          )
        : options;

    return removeDuplicateDeliveryAddresses(
      filteredOptions.map((shipment: Shipment) => ({
        label: getDeliveryLocationLabelAlt(shipment.op),
        value:
          shipment.op?.shipType === DispositionType.ON_DOCKYARD
            ? shipment.op?.chantier._id
            : shipment.op?.shipEntrepotName,
      }))
    );
  }, [shippingRecord?.shipments, shippingRecordDisputes, editMode]);

  const shippingRecordDisputeTypeOptions = useMemo(
    () =>
      getShippingRecordDisputeTypeOptions().filter((option) => {
        if (option.value === ShippingRecordDisputeType.PREPARATION_ORDER) {
          return opsWithNoDispute?.length > 0;
        }
        if (option.value === ShippingRecordDisputeType.DELIVERY) {
          return deliveryLocationsWithNoDispute?.length > 0;
        }
        if (option.value === ShippingRecordDisputeType.SHIPPING_RECORD) {
          return (
            !alreadyHasShippingRecordDispute || editMode !== EditMode.CREATE
          );
        }
        return true;
      }),
    [
      alreadyHasShippingRecordDispute,
      deliveryLocationsWithNoDispute,
      opsWithNoDispute,
      editMode,
    ]
  );

  useEffect(() => {
    form.reset(defaultFormValues);
  }, [form, defaultFormValues]);

  const handleDisputeClose = useCallback(async () => {
    if (!selectedDispute?.id) return;

    await updateShippingRecordDispute(selectedDispute.id, {
      closedAt: new Date(),
    });
  }, [updateShippingRecordDispute, selectedDispute]);

  return {
    form,
    editMode,
    type,
    linkedEntityId,
    media,
    opsWithNoDispute,
    deliveryLocationsWithNoDispute,
    shippingRecordDisputeTypeOptions,
    isFetching,
    dispute,
    createShippingRecordDispute,
    updateShippingRecordDispute,
    handleDisputeClose,
  };
};
